import { useState, useCallback, useEffect } from 'react';
import { Product } from '../../types/Product';
import { 
  addProductToList, 
  deleteShoppingList, 
  updateShoppingList,
  getUserShoppingLists 
} from '../../lib/firebase/firestore';
import { useAuth } from '../useAuth';
import { useSound } from '../useSound';
import type { ShoppingList } from '../../types/ShoppingList';

export const useShoppingList = () => {
  const { user } = useAuth();
  const { playSound } = useSound();
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [lists, setLists] = useState<ShoppingList[]>([]);

  const fetchLists = useCallback(async () => {
    if (!user) {
      setLists([]);
      setSelectedListId(null);
      return;
    }

    try {
      const userLists = await getUserShoppingLists(user.uid);
      setLists(userLists);
      
      // Select first list by default if none selected
      if (!selectedListId && userLists.length > 0) {
        setSelectedListId(userLists[0].id);
      }
    } catch (err) {
      console.error('Error fetching lists:', err);
      setError('Error al cargar las listas');
    }
  }, [user, selectedListId]);

  // Fetch lists on mount and when user changes
  useEffect(() => {
    fetchLists();
  }, [user, fetchLists]);

  const addToList = useCallback(async (product: Product) => {
    if (!user) {
      setError('Debes iniciar sesión para añadir productos a tu lista');
      return;
    }

    if (!selectedListId) {
      setError('Selecciona una lista para añadir productos');
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const selectedList = lists.find(list => list.id === selectedListId);
      if (!selectedList) throw new Error('Lista no encontrada');

      // Check if product already exists in list
      if (selectedList.products.some(p => p.id === product.id)) {
        setError('Este producto ya está en la lista');
        return;
      }

      await updateShoppingList(selectedListId, {
        products: [...selectedList.products, product]
      });
      
      playSound('success');
      await fetchLists(); // Refresh lists after adding product
    } catch (err) {
      console.error('Error adding product to list:', err);
      setError('Error al añadir el producto a la lista');
    } finally {
      setIsAdding(false);
    }
  }, [user, selectedListId, lists, playSound, fetchLists]);

  return {
    lists,
    selectedListId,
    setSelectedListId,
    isAdding,
    isDeleting,
    error,
    addToList,
    handleDelete: deleteShoppingList,
    fetchLists
  };
};